"use client";

import Link from "next/link";
import Image from "next/image";
import { useState, useRef, useEffect } from "react";
import { Project, categoryLabels } from "@/data/portfolio";

interface ProjectCardProps {
  project: Project;
  detailHref?: string;
}

export default function ProjectCard({ project, detailHref }: ProjectCardProps) {
  const [sliderPosition, setSliderPosition] = useState(50);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleMouseDown = () => setIsDragging(true);
  const handleMouseUp = () => setIsDragging(false);

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!containerRef.current) return;

      const rect = containerRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
      setSliderPosition(percentage);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging]);

  const handleTouchStart = () => setIsDragging(true);

  useEffect(() => {
    if (!isDragging) return;

    const handleTouchMove = (e: TouchEvent) => {
      if (!containerRef.current) return;

      const rect = containerRef.current.getBoundingClientRect();
      const x = e.touches[0].clientX - rect.left;
      const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
      setSliderPosition(percentage);
    };

    const handleTouchEnd = () => setIsDragging(false);

    window.addEventListener("touchmove", handleTouchMove);
    window.addEventListener("touchend", handleTouchEnd);

    return () => {
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", handleTouchEnd);
    };
  }, [isDragging]);

  const cardContent = (
    <article className="group flex h-full flex-col overflow-hidden rounded-sm border border-white/10 bg-secondary/80 shadow-[0_0_0_1px_rgba(255,255,255,0.03)] transition-colors duration-300 hover:border-accent/40">
      <div
        ref={containerRef}
        className="relative h-64 w-full cursor-col-resize overflow-hidden select-none sm:h-80"
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
      >
        <Image
          src={project.images.before}
          alt={`ENNE - ${project.alt}`}
          fill
          unoptimized
          className="object-cover grayscale-[60%] brightness-90 transition-[filter,transform] duration-300 ease-out group-hover:grayscale-0 group-hover:brightness-100 group-hover:scale-105 group-active:grayscale-0 group-active:brightness-100 group-active:scale-105"
          sizes="(max-width: 640px) 100vw, 100vw"
          priority={false}
          draggable={false}
        />

        <div
          className="absolute inset-0 overflow-hidden"
          style={{ width: `${sliderPosition}%` }}
        >
          <Image
            src={project.images.after}
            alt={`PÄRAST - ${project.alt}`}
            fill
            unoptimized
            className="object-cover grayscale-[60%] brightness-90 transition-[filter,transform] duration-300 ease-out group-hover:grayscale-0 group-hover:brightness-100 group-hover:scale-105 group-active:grayscale-0 group-active:brightness-100 group-active:scale-105"
            sizes="(max-width: 640px) 100vw, 100vw"
            priority={false}
            draggable={false}
          />
        </div>

        <div
          className="absolute bottom-0 top-0 w-1 bg-accent transition-all duration-75"
          style={{ left: `${sliderPosition}%`, transform: "translateX(-50%)" }}
        >
          <div className="absolute left-1/2 top-1/2 flex h-8 w-8 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-accent shadow-lg cursor-grab active:cursor-grabbing">
            <svg className="h-4 w-4 text-primary" fill="currentColor" viewBox="0 0 24 24">
              <path d="M8 5v14M16 5v14" stroke="currentColor" strokeWidth="2" fill="none" />
            </svg>
          </div>
        </div>

        <span className="pointer-events-none absolute left-3 top-3 rounded-sm bg-gray-600/80 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-white opacity-100 transition-opacity duration-300 group-hover:opacity-0 group-active:opacity-0 sm:left-4 sm:top-4 sm:px-4 sm:py-2 sm:text-sm">
          ENNE
        </span>
        <span className="pointer-events-none absolute right-3 top-3 rounded-sm bg-accent/90 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary opacity-100 transition-opacity duration-300 group-hover:opacity-0 group-active:opacity-0 sm:right-4 sm:top-4 sm:px-4 sm:py-2 sm:text-sm">
          PÄRAST
        </span>
      </div>

      <div className="flex flex-1 flex-col p-6 sm:p-8">
        <div className="mb-4 flex items-center justify-between gap-2">
          <span className="text-xs font-semibold uppercase tracking-wider text-accent">
            {categoryLabels[project.category]}
          </span>
          {project.year && (
            <span className="rounded-sm border border-white/5 bg-primary/70 px-3 py-1 text-xs font-medium text-text-muted">
              {project.year}
            </span>
          )}
        </div>

        <div className="min-h-[3.5rem]">
          {detailHref ? (
            <h3 className="line-clamp-2 font-heading text-xl font-bold text-text-main sm:text-2xl">
              <Link href={detailHref} className="hover:underline">
                {project.title}
              </Link>
            </h3>
          ) : (
            <h3 className="line-clamp-2 font-heading text-xl font-bold text-text-main sm:text-2xl">
              {project.title}
            </h3>
          )}
        </div>

        <p className="mt-3 text-sm leading-relaxed text-text-muted sm:mt-4 sm:text-base">
          {project.description}
        </p>

        {project.tags && project.tags.length > 0 && (
          <div className="mt-4 flex min-h-[2rem] flex-wrap gap-2">
            {project.tags.slice(0, 3).map((tag) => (
              <span
                key={tag}
                className="rounded-sm border border-white/5 bg-primary/70 px-3 py-1 text-xs font-medium text-text-muted"
              >
                {tag}
              </span>
            ))}
          </div>
        )}

        <div className="mt-auto pt-6">
          <Link
            href={project.serviceLink}
            aria-label={`Vaata teenust - ${categoryLabels[project.category]}`}
            title={`Vaata teenust - ${categoryLabels[project.category]}`}
            className="inline-flex items-center rounded-sm bg-accent px-6 py-2.5 text-sm font-semibold text-primary transition-colors duration-200 hover:bg-accent/90"
          >
            Vaata: {categoryLabels[project.category]}
            <span className="ml-2">→</span>
          </Link>
        </div>
      </div>
    </article>
  );

  return cardContent;
}
